import Amplify, { API, Auth, Cache, Storage } from 'aws-amplify';
import config from '../aws-exports.json';
import { emitMetric } from './MetricsCollector';
import { getLocalCoreServicesHeaders } from './LocalCoreServices';

export async function getRequestHeaders (headers) {
  const currentSession = await Auth.currentSession();
  return {
    headers: Object.assign({}, {
      'Bs-Token': currentSession.idToken.jwtToken
    }, headers)
  };
}

export async function getRequestContainerHeaders (headers) {
  const session = await Auth.currentSession();
  return {
    headers: Object.assign({}, {
      'bs-token': `bearer ${session.idToken.jwtToken}`
    }, headers)
  };
}

export async function createVerifierRequestHeaders (profileName) {
  const session = await Auth.currentSession();
  const tokenPayload = session.getIdToken().decodePayload();
  return {
    headers: Object.assign({}, {
      'Content-Type': 'application/json',
      'X-Tenant-Id': `${tokenPayload.logon.toUpperCase()}_${profileName}`,
      authorization: `bearer ${JSON.stringify({
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken()
          })}`
    }
    )
  };
}

export function configureStorage (tenantId) {
  Storage.configure({
    customPrefix: {
      public: `${tenantId}/public/`,
      protected: `${tenantId}/protected/`,
      private: `${tenantId}/private/`
    }
  });
}
async function federatedTokenRefresh () {
  const federatedInfo = await Cache.getItem('federatedInfo');
  const response = await API.post('Cognito', '/oauth2/token', { body: { ...federatedInfo }, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
  console.log('federatedTokenRefresh: ' + response);
  return response;
}

export function configureAmplify (region) {
  region = region || config.region;
  const appClient = JSON.parse(localStorage.getItem('tenant-appClient')) || undefined;
  const oauthRegion = appClient ? JSON.parse(localStorage.getItem('tenant-region')) : region;
  const redirectUrl = config.stage === 'production' || config.stage === 'produs' ? 'https://my.buildsimple.com' : 'https://my.buildsimple-dev.com';
  const endpoints = config[region].api.map(api => ({
    ...api,
    custom_header: async () => getLocalCoreServicesHeaders(api)
  }));
  Amplify.configure({
    Auth: {
      authenticationFlowType: 'USER_SRP_AUTH',
      identityPoolId: config[region].cognito.identitypool,
      region: config[region].cognito.region,
      userPoolId: config[region].cognito.userpool,
      userPoolWebClientId: appClient || config[region].cognito.webclient,
      oauth: {
        domain: config[oauthRegion].oauthDomain,
        scope: [
          'email',
          'openid',
          'aws.cognito.signin.user.admin'
        ],
        redirectSignIn: redirectUrl,
        redirectSignOut: redirectUrl,
        responseType: 'code'
      },
      refreshHandlers: {
        developer: federatedTokenRefresh
      }
    },
    Storage: {
      AWSS3: {
        bucket: config[region].storage.bucket,
        region: config[region].storage.region
      }
    },
    API: { endpoints },
    aws_cloud_logic_custom: endpoints,
    aws_cognito_mfa_configuration: config.mfaConfiguration,
    aws_cognito_mfa_types: config.mfaTypes,
    aws_cognito_signup_attributes: config.signupAttributes,
    aws_cognito_verification_mechanisms: config.verificationMechanisms,
    aws_project_region: region
  });
}

export async function getIdentityId () {
  const user = await Auth.currentAuthenticatedUser({ bypassCache: false });
  const cachedId = localStorage.getItem(`${user.username}.identityId`) || undefined;
  if (cachedId) {
    return cachedId;
  } else {
    const credentials = await Auth.currentUserCredentials();
    localStorage.setItem(`${user.username}.identityId`, credentials.identityId);
    return credentials.identityId;
  }
}

export function getStage () {
  return config.stage;
}

export function getApiOAuth2Endpoint () {
  const tenantRegion = JSON.parse(localStorage.getItem('tenant-region')) || undefined;
  if (!tenantRegion) return undefined;
  return config[tenantRegion].apiOAuth2Endpoint;
}

export function getApiUrl () {
  const tenantRegion = JSON.parse(localStorage.getItem('tenant-region')) || undefined;
  if (!tenantRegion) return undefined;
  return config[tenantRegion].apiUrl;
}

export function getRegion () {
  return config.region;
}

/**
  * @Deprecated
*/
export async function executeWithRetry (callback, retries, attempt = 0) {
  try {
    // if (attempt < 3) throw new Error('test');
    return await callback();
  } catch (error) {
    if (attempt < retries) {
      await sleep(300);
      return await executeWithRetry(callback, retries, ++attempt);
    } else {
      throw error;
    }
  }
}

const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

class RequestQueue {

  constructor (maxConcurrent = 5) {
    this.maxConcurrent = maxConcurrent;
    this.running = 0;
    this.queue = [];
  }

  async add (task) {
    if (this.running >= this.maxConcurrent) {
      await new Promise(resolve => this.queue.push(resolve));
    }

    this.running++;

    try {
      return await task();
    } finally {
      this.running--;

      if (this.queue.length > 0) {
        const next = this.queue.shift();
        next();
      }
    }
  }

}

export const requestQueue = new RequestQueue(3);

async function withTimeout (task, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    return await task(controller.signal);
  } finally {
    clearTimeout(timeout);
  }
}

export async function executeWithRetryAndDedupe (task, id, params) {
  return await dedupe(id, () => _executeWithRetry(task, id, params));
}

async function _executeWithRetry (task, id = 'API', {
  retries = 2,
  delay = 300,
  timeout = 8000,
  backoffFactor = 2
} = {}) {
  let attempt = 0;

  // eslint-disable-next-line no-unmodified-loop-condition
  while (true) {
    try {
      // console.log(`[${id}] Attempt ${attempt + 1}`);

      const result = await requestQueue.add(async () => {
        emitMetric({
          type: 'api_queue',
          running: requestQueue.running,
          queued: requestQueue.queue.length
        });
        try {
          return await withTimeout(task, timeout);
        } finally {
          emitMetric({
            type: 'api_queue',
            running: requestQueue.running,
            queued: requestQueue.queue.length
          });
        }
      });

      // console.log(`[${id}] Success`);
      return result;
    } catch (error) {
      const isAbort = error.name === 'AbortError';

      console.warn(`[${id}] Failed attempt ${attempt + 1}`, {
        error: error.message,
        aborted: isAbort
      });

      if (attempt >= retries || error?.status === 404) {
        console.error(`[${id}] Final failure`);
        throw error;
      }

      emitMetric({
        type: 'api_retry',
        label: id,
        attempt: attempt + 1,
        maxRetries: retries
      });

      const waitTime = delay * Math.pow(backoffFactor, attempt);
      await sleep(waitTime);

      attempt++;
    }
  }
}

const pendingRequests = new Map();

function dedupe (key, fn) {
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  const promise = fn().finally(() => {
    pendingRequests.delete(key);
  });

  pendingRequests.set(key, promise);
  return promise;
}
